import React, { useState, useMemo } from 'react';
import qs from 'qs';
import get from 'lodash/get';
import { Button } from 'components/ui';
import Loading from 'components/shared/Loading';
import useFetch from 'components/shared/hooks/useFetch';
import { routes } from 'constants/config';
import PageTitle from 'components/shared/PageTitle';
import BlankSlate from 'components/shared/index/blankslate';
import errorSrc from 'images/error.svg';
import TemplateCreationModal from 'components/content/TemplateCreationModal';
import TemplateTable from './TemplateTable/TemplateTable';
import TemplateEmptyState from './TemplateEmptyState';

const DEFAULT_SORT = { direction: 'asc', column: 'title' };
const DEFAULT_PAGE = 1;
const DEFAULT_SEARCH = qs.stringify(
  { sort: DEFAULT_SORT, page: DEFAULT_PAGE },
  { encodeValuesOnly: true, addQueryPrefix: true },
);

const PHASES = {
  ERROR: 'ERROR',
  SUCCESS: 'SUCCESS',
  EMPTY: 'EMPTY',
  LOADING: 'LOADING',
};

function getSortFromSortType(sortType) {
  if (sortType === 'numeric') {
    return 'desc';
  }

  return 'asc';
}

function getPhase({ data, loading, error }) {
  if (loading) {
    return PHASES.LOADING;
  }

  if (error) {
    return PHASES.ERROR;
  }

  if (data.templates.length < 1) {
    return PHASES.EMPTY;
  }

  return PHASES.SUCCESS;
}

const useQueryParams = search => {
  // We place api filters to the url and use them to set initial table filters
  return useMemo(() => {
    const { sort, page } = qs.parse(search, { ignoreQueryPrefix: true });
    return { sort, page: parseInt(page, 10) || 1 };
  }, [search]);
};

const setQueryParams = (history, params) => {
  history.push({
    search: qs.stringify(params, { encodeValuesOnly: true }),
  });
};

const Error = () => (
  <div className="ContentLibrary--blankslate">
    <BlankSlate
      image={errorSrc}
      title={t('contents.library.element_library.error.title')}
      description={t('contents.library.element_library.error.description')}
    />
  </div>
);

const TemplateLibrary = ({ location: { search: urlSearch }, history }) => {
  const search = urlSearch || DEFAULT_SEARCH;
  const fetchData = useFetch(`${routes.CONTENT.LIBRARY_TEMPLATES}${search}`);
  const phase = getPhase(fetchData);
  const { data = { templates: [] }, loading } = fetchData;
  const { sort, page } = useQueryParams(search);
  const perPage = get(data, 'filters.perPage');
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const closeTemplateModal = () => {
    setTemplateModalVisible(false);
  };

  const setSort = ({ column, sortType }) => {
    const { direction: sortDirection, column: sortColumn } = sort;
    let newSort = { direction: getSortFromSortType(sortType), column };
    if (column === sortColumn) {
      newSort = {
        direction: sortDirection === 'desc' ? 'asc' : 'desc',
        column,
      };
    }

    setQueryParams(history, { sort: newSort, page, perPage });
  };

  const setPage = newPage => {
    setQueryParams(history, { sort, page: newPage, perPage });
  };

  const setPerPage = newPerPage => {
    setQueryParams(history, { sort, page: 1, perPage: newPerPage });
  };
  return (
    <>
      <PageTitle title={t('contents.library.template_library.title')} />

      {phase === PHASES.LOADING && <Loading loading />}
      {(phase === PHASES.SUCCESS || phase === PHASES.EMPTY) && (
        <div className="d-f ai-c m-fd-c jc-sb mt-20">
          {t('contents.library.template_library.template_count', {
            template_count: data.totalTemplates,
          })}
          <Button
            icon="add"
            onClick={() => setTemplateModalVisible(true)}
            className="trk_button_readiness_content-library_lesson-template_add"
          >
            {t('contents.library.template_library.new_template')}
          </Button>
        </div>
      )}
      {phase === PHASES.SUCCESS && (
        <TemplateTable
          loading={loading}
          templates={data.templates}
          sort={sort}
          page={page}
          perPage={perPage}
          totalPages={data.totalPages}
          setSort={setSort}
          setPage={setPage}
          setPerPage={setPerPage}
        />
      )}
      {templateModalVisible && (
        <TemplateCreationModal closeModal={closeTemplateModal} />
      )}
      {phase === PHASES.EMPTY && <TemplateEmptyState />}
      {phase === PHASES.ERROR && <Error />}
    </>
  );
};

export default TemplateLibrary;
